import {
  Button
} from "@mui/material";
import { Formik } from "formik";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import ExportButton from "../commons/export/ExportButton";
import FormikDatePicker from "../commons/formik/FormikDatePicker";
import { tableConfig, tableStyles } from "../commons/table/tableConfig";
import TitleContainer from "../commons/table/utilsTable/TitleWithFetchData";
import { StyledCuentaCorrienteTable } from "../styled/CuentaCorriente";
import CuentaCorrienteAccountData from "./CuentaCorrienteAccountData";
import ReporteEstadoButton from "./ReporteEstadoButton";

const initialValues = {
  fechaDesde: null,
  fechaHasta: null,
};

const validationSchema = Yup.object().shape({
  fechaDesde: Yup.date()
    .nullable()
    .typeError("Fecha inválida")
    .test({
      name: "fechaDesde",
      message: "La fecha desde debe ser menor o igual a la fecha hasta",
      test: function (value) {
        if (!value || !this.parent.fechaHasta) {
          return true;
        }
        return value <= this.parent.fechaHasta;
      },
    }),

  fechaHasta: Yup.date()
    .nullable()
    .typeError("Fecha inválida")
    .test({
      name: "fechaHasta",
      message: "La fecha hasta debe ser mayor o igual a la fecha desde",
      test: function (value) {
        if (!value || !this.parent.fechaDesde) {
          return true;
        }
        return value >= this.parent.fechaDesde;
      },
    }),
});

const CuentaCorrienteTable = ({
  title,
  data,
  loading,
  fetchData,
  noRefresh,
  secondaryTitle,
  handleFilter,
  fechaDesde,
  fechaHasta,
}) => {
  const [tableData, setTableData] = useState([]);
  const [saldoALaFecha, setSaldoALaFecha] = useState(0);
  const [filterModel, setfilterModel] = useState(initialValues);
  const [formResetKey, setFormResetKey] = useState(Date.now());

  useEffect(() => {
    if (data?.cuentas) {
      const tableDataModel = data.cuentas.map((cuenta) => {
        return {
          ...cuenta,
          masaSalarial: new Intl.NumberFormat("es-AR").format(
            cuenta.masaSalarial || 0
          ),
          cuota: new Intl.NumberFormat("es-AR").format(cuenta.cuota || 0),
          ffep: new Intl.NumberFormat("es-AR").format(cuenta.ffep || 0),
          pago: new Intl.NumberFormat("es-AR").format(cuenta.pago || 0),
          interes: new Intl.NumberFormat("es-AR").format(cuenta.interes || 0),
          saldoPeriodo: new Intl.NumberFormat("es-AR").format(
            cuenta.saldoPeriodo || 0
          ),
        };
      });
      setTableData(tableDataModel);
      setSaldoALaFecha(
        new Intl.NumberFormat("es-AR").format(data?.saldoFecha) ||
        data?.saldoFecha
      );
    }
  }, [data]);

  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      setfilterModel({
        fechaDesde: fechaDesde,
        fechaHasta: fechaHasta,
      });
    }
  }, [fechaDesde, fechaHasta]);

  const columns = [
    {
      header: "Periodo Fiscal",
      accessorKey: "periodoFiscal",

    },
    {
      header: "Periodo Cobertura",
      accessorKey: "periodoCobertura",

    },
    {
      header: "Cápitas",
      accessorKey: "capitas",
    },
    {
      header: "Masa Salarial",
      accessorKey: "masaSalarial",
      size: 110,
      maxSize: 110,
    },
    {
      header: "Suma Fija Declarada",
      accessorKey: "sumaFijaDeclarada",

    },
    {
      header: "Alícuota Declarada",
      accessorKey: "alicuotaDeclarada",
    },
    {
      header: "Suma Fija Facturada",
      accessorKey: "sumaFijaFacturada",
    },
    {
      header: "Alícuota Facturada",
      accessorKey: "alicuotaFacturada",
    },
    {
      header: "Cuota",
      accessorKey: "cuota",
      size: 110,
      maxSize: 110,
    },
    {
      header: "FFEP",
      accessorKey: "ffep",
    },
    {
      header: "Pago",
      accessorKey: "pago",
    },
    {
      header: "Intereses por Mora ",
      accessorKey: "interes",
      maxSize: 80,
    },
    {
      header: "Saldo",
      accessorKey: "saldoPeriodo",
      size: 110,
      maxSize: 110,
    },
  ];

  const table = useMaterialReactTable({
    ...tableConfig,
    columns,
    data: tableData,
    layoutMode: "grid",
    state: { isLoading: loading },
    style: { ...tableStyles.container },
    enableColumnActions: false,
    renderToolbarInternalActions: () => (
      <CuentaCorrienteAccountData />
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <>
        <div>
          <StyledCuentaCorrienteTable.TitleContainer>
            <div>
              <StyledCuentaCorrienteTable.ToolbarActionsContainer>
                <ExportButton data={tableData} columns={columns} title="Cuenta Corriente" />
                <ReporteEstadoButton />
              </StyledCuentaCorrienteTable.ToolbarActionsContainer>
            </div>
          </StyledCuentaCorrienteTable.TitleContainer>
          <TitleContainer
            title={title}
            noRefresh={noRefresh}
            fetchData={handleRefresh}
            saldoALaFecha={saldoALaFecha}
            subTitle={secondaryTitle}
          />
          <Formik
            key={formResetKey}
            initialValues={filterModel}
            validationSchema={validationSchema}
            onSubmit={handleFilter}
            enableReinitialize={true}
          >
            {({ values }) => (
              <StyledCuentaCorrienteTable.FilterContainer>
                <StyledCuentaCorrienteTable.FilterItem>
                  <FormikDatePicker
                    name="fechaDesde"
                    label="Fecha desde"
                    ampm="false"
                    format="dd/MM/yyyy"
                    variant="inline"
                  />
                </StyledCuentaCorrienteTable.FilterItem>
                <StyledCuentaCorrienteTable.FilterItem>
                  <FormikDatePicker
                    name="fechaHasta"
                    label="Fecha hasta"
                    ampm="false"
                    format="dd/MM/yyyy"
                    variant="inline"
                  />
                </StyledCuentaCorrienteTable.FilterItem>
                <StyledCuentaCorrienteTable.ButtonContainer>
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    disabled={
                      values.fechaDesde == null ||
                      values.fechaHasta == null
                    }
                  >
                    Buscar
                  </Button>
                </StyledCuentaCorrienteTable.ButtonContainer>
              </StyledCuentaCorrienteTable.FilterContainer>
            )}
          </Formik>
        </div>
      </>
    ),
  });

  const handleRefresh = () => {
    setfilterModel(initialValues);
    setFormResetKey(Date.now());
    table.resetColumnFilters();
    fetchData();
  };

  return (
    <div>
      <MaterialReactTable table={table} />
    </div>
  );
};

export default CuentaCorrienteTable;
